import React, { ReactNode } from 'react';
import { styled, Tab, tabClasses, Tabs, tabsClasses, type TabsProps } from '@mui/material';

import { borders } from '@/theme/constants';

export const VerticalTab = styled(Tab)(({ theme }) => {
  const { spacing, palette, typography } = theme;

  return {
    whiteSpace: 'nowrap',
    padding: spacing(0.75, 3),
    minHeight: 'unset',
    textTransform: 'none',
    alignItems: 'start',
    ...typography.body2,

    '&:not(.Mui-selected)': {
      color: palette.text.primary,
      fontWeight: typography.fontWeightRegular
    },

    [`&:hover`]: {
      color: palette.primary.main
    },

    [`&:hover::before`]: {
      content: '""',
      position: 'absolute',
      width: '1px',
      top: 0,
      bottom: 0,
      insetInlineStart: 0,
      backgroundColor: palette.grey[500]
    },

    [`&.${tabClasses.textColorPrimary}.${tabClasses.selected}`]: {
      color: palette.primary.main,
      fontWeight: typography.fontWeightMedium
    },

    '&.Mui-disabled': {
      color: palette.grey[700]
    }
  };
}) as typeof Tab;

export const VerticalTabs = styled((props: TabsProps) => <Tabs {...props} orientation="vertical" />)(
  ({ theme: { spacing } }) => ({
    minHeight: 'unset',

    [`& .${tabsClasses.indicator}`]: {
      insetInlineStart: 0
    },

    [`& .${tabsClasses.flexContainer}`]: {
      gap: spacing(2)
    }
  })
) as typeof Tabs;

export const HorizontalTabs = styled((props: TabsProps) => <Tabs {...props} orientation="horizontal" />)(
  ({ theme: { spacing, palette } }) => ({
    minHeight: spacing(5),

    '& .MuiTabs-flexContainer': {
      borderBottom: borders.default,
      borderColor: palette.grey[400]
    }
  })
) as typeof Tabs;

export const HorizontalTab = styled(Tab)(({ theme: { spacing, typography, palette } }) => ({
  textTransform: 'none',
  borderTopRightRadius: spacing(0.5),
  borderTopLeftRadius: spacing(0.5),
  padding: spacing(1, 2),
  minHeight: spacing(5),
  color: palette.text.primary,
  ...typography.body2,

  '&.Mui-selected': {
    backgroundColor: palette.primary[100],
    color: palette.primary.main,
    fontWeight: typography.fontWeightMedium
  },
  '&.Mui-disabled': {
    color: palette.grey[700]
  }
})) as typeof Tab;

export const TabPanel = ({
  children,
  value,
  currentValue
}: {
  children?: ReactNode;
  value: unknown;
  currentValue: unknown;
}) => value === currentValue && children;
